import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid2,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  FormControlLabel,
  InputAdornment
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import thLocale from 'date-fns/locale/th';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import projectService, { Project, ProjectFormData } from '../services/projectService';
import PageHeader from '../components/common/PageHeader';
import InputField from '../components/common/FormFields/InputField';
import IOSSwitch from '../components/common/IOSSwitch';
import { projectValidationSchema } from '../validations/projectValidation';

// สถานะโครงการ
enum ProjectStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED'
}

const ProjectEdit: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [project, setProject] = useState<Project | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [formData, setFormData] = useState<ProjectFormData>({
    code: '',
    name: '',
    description: '',
    status: ProjectStatus.NOT_STARTED,
    isActive: true,
    startDate: '',
    endDate: '',
    workStartTime: '08:00',
    workEndTime: '17:00',
    maxLateTime: '00:30',
    latitude: '',
    longitude: '',
    radius: '5',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (id) {
      fetchProjectData();
    }
  }, [id]);

  const fetchProjectData = async () => {
    try {
      if (id) {
        setLoading(true);
        const projectData = await projectService.getProjectById(id);
        setProject(projectData);
        
        console.log('Project data from API:', projectData);
        console.log('isActive value:', projectData.isActive, typeof projectData.isActive);
        
        // แปลงข้อมูลโครงการเป็นรูปแบบของฟอร์ม
        setFormData({
          code: projectData.code || '',
          name: projectData.name || '',
          description: projectData.description || '',
          status: projectData.status || ProjectStatus.NOT_STARTED,
          isActive: Boolean(projectData.isActive),
          startDate: projectData?.startDate ? new Date(projectData.startDate).toISOString() : '',
          endDate: projectData?.endDate ? new Date(projectData.endDate).toISOString() : '',
          workStartTime: projectData.workStartTime || '08:00',
          workEndTime: projectData.workEndTime || '17:00',
          maxLateTime: projectData.maxLateTime || '00:30',
          latitude: projectData.latitude !== undefined ? String(projectData.latitude) : '',
          longitude: projectData.longitude !== undefined ? String(projectData.longitude) : '',
          radius: projectData.radius !== undefined ? String(projectData.radius) : '5',
        });
        
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching project data:', error);
      setSnackbar({
        open: true,
        message: 'เกิดข้อผิดพลาดในการดึงข้อมูลโครงการ',
        severity: 'error',
      });
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // ล้างข้อความแจ้งเตือนเมื่อผู้ใช้แก้ไขข้อมูล
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleDateChange = (name: string, date: Date | null) => {
    setFormData(prev => ({
      ...prev,
      [name]: date ? date.toISOString() : ''
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateField = async (field: string, value: any) => {
    try {
      await projectValidationSchema.validateAt(field, { [field]: value });
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
      return true;
    } catch (error: any) {
      setValidationErrors(prev => ({ ...prev, [field]: error.message }));
      return false;
    }
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    handleChange(e);
    await validateField(name, value);
  };

  const handleDateInputChange = async (name: string, date: Date | null) => {
    handleDateChange(name, date);
    await validateField(name, date ? date.toISOString() : '');
  };

  const handleSwitchInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    handleSwitchChange(e);
    await validateField(name, checked);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.code.trim()) {
      newErrors.code = 'กรุณากรอกรหัสโครงการ';
    }
    
    if (!formData.name.trim()) {
      newErrors.name = 'กรุณากรอกชื่อโครงการ';
    }
    
    if (!formData.description.trim()) {
      formData.description = 'ไม่มีรายละเอียดโครงการ';
      // newErrors.description = 'กรุณากรอกรายละเอียดโครงการ';
    }

    if (!formData.workStartTime.trim()) {
      newErrors.workStartTime = 'กรุณากรอกเวลาเริ่มงาน';
    }
    
    if (!formData.workEndTime.trim()) {
      newErrors.workEndTime = 'กรุณากรอกเวลาเลิกงาน';
    }

    if (!formData.maxLateTime.trim()) {
      newErrors.maxLateTime = 'กรุณากรอกเวลาสูงสุดที่สามารถเข้างานสาย';
    }
    
    if (formData.latitude && isNaN(Number(formData.latitude))) {
      newErrors.latitude = 'ละติจูดต้องเป็นตัวเลข';
    }
    
    if (formData.longitude && isNaN(Number(formData.longitude))) {
      newErrors.longitude = 'ลองจิจูดต้องเป็นตัวเลข';
    }
    
    if (!formData.radius.trim()) {
      newErrors.radius = 'กรุณากรอกรัศมี';
    } else if (isNaN(Number(formData.radius))) {
      newErrors.radius = 'รัศมีต้องเป็นตัวเลข';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      if (id) {
        await projectService.updateProject(id, formData);
        setSnackbar({
          open: true,
          message: 'อัปเดตโครงการสำเร็จ',
          severity: 'success',
        });
        
        // รอสักครู่แล้วกลับไปหน้ารายละเอียดโครงการ
        setTimeout(() => {
          navigate(`/projects/${id}`);
        }, 2000);
      }
    } catch (error) {
      console.error('Error updating project:', error);
      setSnackbar({
        open: true,
        message: 'เกิดข้อผิดพลาดในการอัปเดตโครงการ',
        severity: 'error',
      });
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(`/projects/${id}`);
  };

  const handleGotoProject = () => {
    navigate(`/projects`);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false,
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 2, md: 4 }, maxWidth: '100%' }}>

      <PageHeader
        title="แก้ไขโครงการ"
        breadcrumb={{
          items: [
            { label: "รายละเอียดโครงการ", onClick: handleGotoProject },
            { label: "แก้ไขโครงการ" },
          ],
        }}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          p: { xs: 2, sm: 2, md: 4 },
          borderRadius: 2,
          border: "1px solid #e0e0e0",
          maxWidth: "100%",
        }}>
        <form onSubmit={handleSubmit}>
          <Grid2 container spacing={4}>

            <Grid2 size={12} direction="row" sx={{ display: 'flex', justifyContent: 'space-between' }} >
              <Grid2 size={12}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  แก้ไขโครงการ: {project?.name}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 'auto', sm: 6 }} container direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 2 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} /> : null}
                  sx={{
                    backgroundColor: 'custom.green',
                    '&:hover': { backgroundColor: 'custom.greenDark' }
                  }}
                >
                  {saving ? 'กำลังบันทึก...' : 'บันทึก'}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleGotoProject}
                  disabled={saving}
                  sx={{
                    backgroundColor: 'custom.grey',
                    '&:hover': { backgroundColor: 'custom.greyDark' }
                  }}
                >
                  ยกเลิก
                </Button>
              </Grid2>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }}>
              <InputField
                label="ชื่อโครงการ"
                labelSuffix=":"
                name="name"
                required
                value={formData.name}
                onChange={handleInputChange}
                onBlur={(e) => validateField('name', e.target.value)}
                error={!!errors.name || !!validationErrors.name}
                helperText={errors.name || validationErrors.name}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }}>
              <InputField
                label="รหัสโครงการ"
                labelSuffix=":"
                name="code"
                required
                value={formData.code}
                onChange={handleInputChange}
                onBlur={(e) => validateField('code', e.target.value)}
                error={!!errors.code || !!validationErrors.code}
                helperText={errors.code || validationErrors.code}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2"
                sx={{ mb: 1, fontWeight: 500, color: "#333" }}>รายละเอียดโครงการ</Typography>
              <TextField
                fullWidth
                placeholder="รายละเอียดโครงการ"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                onBlur={(e) => validateField('description', e.target.value)}
                error={!!errors.description || !!validationErrors.description}
                helperText={errors.description || validationErrors.description}
                multiline
                rows={3}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} container spacing={1} sx={{ display: 'flex', justifyContent: 'start' }}>
              <Grid2 size={{ xs: 12 }}>
                <Typography variant="body2"
                  sx={{ mb: 1, fontWeight: 500, color: "#333" }}>สถานะโครงการ</Typography>
                <TextField
                  fullWidth
                  select
                  placeholder="สถานะโครงการ"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  onBlur={(e) => validateField('status', e.target.value)}
                  error={!!errors.status || !!validationErrors.status}
                  helperText={errors.status || validationErrors.status}
                  required
                >
                  <MenuItem value={ProjectStatus.NOT_STARTED}>ยังไม่เริ่ม</MenuItem>
                  <MenuItem value={ProjectStatus.IN_PROGRESS}>กำลังดำเนินการ</MenuItem>
                  <MenuItem value={ProjectStatus.COMPLETED}>เสร็จสิ้น</MenuItem>
                </TextField>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6 }} sx={{ ml: 2 }}>
                <FormControlLabel
                  control={
                    <IOSSwitch
                      checked={formData.isActive}
                      onChange={handleSwitchInputChange}
                      name="isActive"
                      sx={{ mr: 1 }}
                    />
                  }
                  label={formData.isActive ? "เปิดใช้งานโครงการ" : "ปิดใช้งานโครงการ"}
                />
              </Grid2>
            </Grid2>

            <Grid2 container spacing={2} size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'start' }} >

              <Grid2 container spacing={2} size={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={thLocale}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2"
                      sx={{ mb: 1, fontWeight: 500, color: "#333" }}>วันที่เริ่มโครงการ</Typography>
                    <DatePicker
                      value={formData.startDate ? new Date(formData.startDate) : null}
                      onChange={(date) => handleDateInputChange('startDate', date)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.startDate || !!validationErrors.startDate,
                          helperText: errors.startDate || validationErrors.startDate,
                          InputLabelProps: {
                            shrink: false
                          }
                        }
                      }}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2"
                      sx={{ mb: 1, fontWeight: 500, color: "#333" }}>วันที่สิ้นสุดโครงการ</Typography>
                    <DatePicker
                      value={formData.endDate ? new Date(formData.endDate) : null}
                      onChange={(date) => handleDateInputChange('endDate', date)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.endDate || !!validationErrors.endDate,
                          helperText: errors.endDate || validationErrors.endDate,
                          InputLabelProps: {
                            shrink: false
                          }
                        }
                      }}
                    />
                  </Grid2>
                </LocalizationProvider>
              </Grid2>

              <Grid2 container spacing={2} size={12}>
                <Grid2 size={{ xs: 12, sm: 4 }}>
                  <InputField
                    label="เวลาเข้างาน"
                    labelSuffix=":"
                    name="workStartTime"
                    value={formData.workStartTime}
                    onChange={handleInputChange}
                    onBlur={(e) => validateField('workStartTime', e.target.value)}
                    error={!!errors.workStartTime || !!validationErrors.workStartTime}
                    helperText={errors.workStartTime || validationErrors.workStartTime}
                    required
                    placeholder="เช่น 08:00"
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 4 }}>
                  <InputField
                    label="เวลาเลิกงาน"
                    labelSuffix=":"
                    name="workEndTime"
                    value={formData.workEndTime}
                    onChange={handleInputChange}
                    onBlur={(e) => validateField('workEndTime', e.target.value)}
                    error={!!errors.workEndTime || !!validationErrors.workEndTime}
                    helperText={errors.workEndTime || validationErrors.workEndTime}
                    required
                    placeholder="เช่น 17:00"
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 4 }}>
                  <InputField
                    label="เวลาที่สามารถเข้างานสาย"
                    labelSuffix=":"
                    name="maxLateTime"
                    value={formData.maxLateTime}
                    onChange={handleInputChange}
                    onBlur={(e) => validateField('maxLateTime', e.target.value)}
                    error={!!errors.maxLateTime || !!validationErrors.maxLateTime}
                    helperText={errors.maxLateTime || validationErrors.maxLateTime}
                    required
                    placeholder="เช่น 00:30"
                  />
                </Grid2>
              </Grid2>

              <Grid2 container spacing={2} size={12}>
                <Grid2 size={{ xs: 12, sm: 4 }}>
                  <InputField
                    label="พิกัดละติจูด"
                    labelSuffix=":"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleInputChange}
                    onBlur={(e) => validateField('latitude', e.target.value)}
                    error={!!errors.latitude || !!validationErrors.latitude}
                    helperText={errors.latitude || validationErrors.latitude}
                    required
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 4 }}>
                  <InputField
                    label="พิกัดลองจิจูด"
                    labelSuffix=":"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleInputChange}
                    onBlur={(e) => validateField('longitude', e.target.value)}
                    error={!!errors.longitude || !!validationErrors.longitude}
                    helperText={errors.longitude || validationErrors.longitude}
                    required
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 4 }}>
                  <InputField
                    label="ระยะการเข้า - ออก"
                    labelSuffix=":"
                    name="radius"
                    value={formData.radius}
                    onChange={handleInputChange}
                    onBlur={(e) => validateField('radius', e.target.value)}
                    error={!!errors.radius || !!validationErrors.radius}
                    helperText={errors.radius || validationErrors.radius}
                    required
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">เมตร</InputAdornment>,
                      },
                    }}
                  />
                </Grid2>
              </Grid2>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Box>
                <Typography variant="body2"
                  sx={{ mb: 1, fontWeight: 500, color: "#333" }}>แผนที่แสดงพิกัดโครงการ</Typography>
                <Box sx={{ 
                  width: '100%', 
                  height: '400px', 
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  overflow: 'hidden'
                }}>
                  <iframe
                    title="Project Location Map"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    style={{ border: 0 }}
                    src={`https://maps.google.com/maps?q=${formData.latitude},${formData.longitude}&z=15&output=embed`}
                    allowFullScreen
                  />
                </Box>
                <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                  * แผนที่จะอัพเดทอัตโนมัติเมื่อมีการเปลี่ยนแปลงค่าละติจูดหรือลองจิจูด
                </Typography>
              </Box>
            </Grid2>
          </Grid2>
        </form>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectEdit; 